import { Observable } from 'rxjs';
import { tap } from 'rxjs';

import { BasicInterfaceTypeEnum, CatsNoBillCodeDescriptionEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, findUnique, syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, convertSapDateToWeekDayString, getDayString } from '@shared-helpers/date-helpers';
import { addDecimals, safeCompare } from '@shared-helpers/string-number-helpers';
import { ISkfConfigEntity } from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import { IEquipmentBillingSummaryRow, IEquipmentPayrollSummaryRow, ITableConfigEntity } from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';
import {
  BasicInterface,
  IBasicInterfaceContext,
  IBasicInterfaceContract,
  IBasicInterfaceNotProductionTextContract,
} from './basicInterface';
import { IOutgoingCatsArrays } from './catsDB';
import { ProductionEquipmentCatsRecord } from './productionEquipmentCatsRecord';
import {
  makeBillingEquipmentWeeklyConfig,
  makeBillingProductionQuantityAndProductionEquipmentDailyConfig,
} from './table-configs/billing-tables.config';
import { makePayrollEquipmentWeeklyConfig } from './table-configs/payroll-tables.config';

export class ProductionEquipment
  extends BasicInterface
  implements IBasicInterfaceContract, IBasicInterfaceNotProductionTextContract
{
  //this class handles rendering summary and details for quantity based SKF CAT records.
  public readonly billCodeType = BasicInterfaceTypeEnum.ProductionEquipment;

  //calculated fields
  display: string;
  billingGrid: any[];
  payrollGrid: any[];
  detailPayneRecords: any[];
  total: any = 0;
  payrollTotal = '0';
  tooltip: string;
  costCenters: string[] = [];
  filteredCostCenter?: string;

  //fields from SAP
  /* eslint-disable @typescript-eslint/naming-convention -- These are names from SAP, not our code, so it won't comply with our naming conventions */
  CustGroup: string;
  CondGroup: string;
  SKF: string;
  Unit: string;
  ac_unit: string;
  BillCode: string;
  Type: string;

  //Descriptions - Payroll uses SKFDescription and everything else uses CustDesc
  CustDesc: string;
  SKFDescription: string;

  Printtocust: string;
  ShowOnScreen: string;
  Mode: string;
  Readonly: boolean;
  /* eslint-enable @typescript-eslint/naming-convention */

  constructor(
    public datahub: DatahubService,
    skf: ISkfConfigEntity,
    context: IBasicInterfaceContext,
  ) {
    super(context);
    syncObjectProperties(skf, this);
    this.setDisplayProperty();
    this.setConfig();
  }

  private setConfig(): void {
    if (this.isPayroll) {
      this.__config = makePayrollEquipmentWeeklyConfig();
    } else if (this.isBilling) {
      this.__config = makeBillingEquipmentWeeklyConfig(this.Readonly);
    }
  }

  addCostCenter(costCenter: string) {
    this.costCenters.push(costCenter);
  }

  private setDisplayProperty(): void {
    if (this.isPayroll) {
      this.display = this.SKFDescription;
    } else {
      this.display = this.CustDesc;
    }
    this.tooltip = this.SKF;
  }

  setCatsRecords(catsRecords: ICatRecord[]): void {
    this.catsRecords = catsRecords;
    this.visibleCatsRecords = catsRecords.filter((x) => Number(x.Quantity) > 0);
    this.handleCatsRecordsSet();
  }

  handleCatsRecordsSet() {
    //once cats records are set, we should recalculate totals and re-render the object in the view. Keeping this to employee level should allow us to refresh just this entity when records are created destroyed or modified.
    if (this.isPayroll) this.calculatePayrollGrid(this.filteredCostCenter);
    else this.calculateBillingGrid(this.filteredTimesheet);
    this.checkApprovalStatus(this.catsRecords);
  }

  filterByTimesheet(timesheetNo: string): void {
    if (!timesheetNo) {
      this.filteredTimesheet = '';
      this.calculateBillingGrid('');
      return;
    }
    this.filteredTimesheet = timesheetNo;
    this.calculateBillingGrid(timesheetNo);
  }

  filterByCostCenter(costCenter?: string) {
    this.filteredCostCenter = costCenter;
    this.handleCatsRecordsSet();
  }

  calculatePayrollGrid(costCenter?: string) {
    const row = this.makeEmptyEquipmentPayrollSummaryRow();
    this.total = 0;
    let recordPool = this.visibleCatsRecords;
    if (!arrayIsTruthy(recordPool)) return (this.payrollGrid = [row]);
    if (costCenter) recordPool = recordPool.filter((x) => safeCompare(x.__costCenter ?? '', costCenter));
    recordPool.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const hours = record.Quantity;
      row[weekDay] = addDecimals(hours, row[weekDay]);
      this.total = addDecimals(this.total, hours);
    });
    row.total = this.total;
    this.payrollTotal = this.total;
    return (this.payrollGrid = [row]);
  }

  calculateBillingGrid(timesheetNo?: string) {
    let recordPool = this.visibleCatsRecords;
    if (timesheetNo) recordPool = recordPool.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    if (!arrayIsTruthy(recordPool)) return (this.billingGrid = [this.makeBillingRow(this.BillCode, [])]);
    const codes = findUnique<string>(recordPool, 'Zmatnr', true);
    this.total = 0;
    return (this.billingGrid = codes.map((x) => {
      return this.makeBillingRow(x, recordPool);
    }));
  }

  makeBillingRow(code: string, recordPool: ICatRecord[]): IEquipmentBillingSummaryRow {
    const row = this.makeEmptyEquipmentBillingSummaryRow(code);
    const records = recordPool.filter((x) => x.Zmatnr === code);
    records.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const quantity = Number(record.Quantity); //can't perform operations over strings.
      row[weekDay] = addDecimals(row[weekDay], quantity);
      this.total = addDecimals(this.total, quantity);
      row.total = addDecimals(row.total, quantity);
    });
    return row;
  }

  showBulkAdd() {
    const config = this.getBillingDetailPayneConfig();
    this.datahub.detailPayneHandler.showBulkAdd(this, config);
  }

  makeEmptyEquipmentPayrollSummaryRow(): IEquipmentPayrollSummaryRow {
    //provides the default values for the employee payroll summary view.
    return {
      equipment: this.display,
      total: '0',
      sun: '0',
      mon: '0',
      tue: '0',
      wed: '0',
      thu: '0',
      fri: '0',
      sat: '0',
    };
  }

  makeEmptyEquipmentBillingSummaryRow(material: string) {
    //this should probably return equipmentBillingSummaryRow but that interface does not have material on it!
    let billcode = material;
    if (!billcode) billcode = CatsNoBillCodeDescriptionEnum.Unassigned;
    return {
      equipment: this.display,
      total: '0',
      material: material,
      billCode: billcode,
      sun: '0',
      mon: '0',
      tue: '0',
      wed: '0',
      thu: '0',
      fri: '0',
      sat: '0',
    };
  }

  launchPayrollDetailPayneHandler(row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    this.getPayrollDetailPayneValues(row, column);
    this.datahub.detailPayneHandler.showPayrollEquipment(this);
  }

  getPayrollDetailPayneValues(_row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.focusedDay = this.datahub.billingTimesheet.getDate(column.key);
    const config = this.getPayrollDetailPayneConfig();
    this.detailPayneRecords = this.visibleCatsRecords
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new ProductionEquipmentCatsRecord(this, config).fillStateFromSap(x));
    this.datahub.detailPayneHandler.showBillingProductionQuantity(this);
  }

  getBillingDetailPayneValues(row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.focusedDay = this.datahub.billingTimesheet.getDate(column.key);
    const material = this.billingGrid[row].material;
    const records = this.visibleCatsRecords.filter((record) => material === record.Zmatnr);
    const config = this.getBillingDetailPayneConfig();
    this.detailPayneRecords = records
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new ProductionEquipmentCatsRecord(this, config).fillStateFromSap(x));
    this.datahub.detailPayneHandler.showBillingProductionQuantity(this);
  }

  getBillingDetailPayneConfig() {
    //when the detail payne is rendered for this employee, this function provides the appropriate drop down values.
    return makeBillingProductionQuantityAndProductionEquipmentDailyConfig(this.Readonly);
  }

  getPayrollDetailPayneConfig() {
    //when the detail payne is rendered for this employee, this function provides the appropriate drop down values.
    return makeBillingProductionQuantityAndProductionEquipmentDailyConfig(this.Readonly);
  }

  getPlaceholderEmployee() {
    return this.datahub.loggedInUserEmployeeId;
  }

  addNewRecord(config: ITableConfigEntity[]): ProductionEquipmentCatsRecord {
    const record = new ProductionEquipmentCatsRecord(this, config);
    return record.getRequiredObject(this);
  }

  handleMassBillCodeChange$(current: string, desired: string, timesheetNo: string) {
    let records = this.catsRecords.filter((x) => x.Zmatnr === current);
    if (timesheetNo) records = records.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    const update = records.map((x) => {
      return {
        Zmatnr: desired,
        Counter: x.Counter,
      };
    });
    const object: IOutgoingCatsArrays = {
      create: [],
      delete: [],
      update: update,
      all: update,
    };
    return this.save$(object);
  }

  save$(records: IOutgoingCatsArrays): Observable<ICatRecord[]> {
    return this.datahub.catsDB.syncSingleEntityWithCats$(records).pipe(
      tap((catsRecords: ICatRecord[]) => {
        catsRecords.forEach(
          (x) => (x.__costCenter = this.datahub.contracts.findContractByServiceOrder(x.RecOrder)?.getCostCenter() ?? ''),
        );
        records.update
          .concat(records.delete)
          .forEach((record) => (this.catsRecords = this.catsRecords.filter((x) => x.Counter !== record.Counter)));
        this.catsRecords = this.catsRecords.concat(catsRecords);
        this.setCatsRecords(this.catsRecords);
      }),
    );
  }
}
